import { DayOfWeek } from "@src/appV2/InterviewSettings/constants";

const DAY_ABBREVIATIONS: Record<string, string> = {
  [DayOfWeek.MONDAY]: "Mon",
  [DayOfWeek.TUESDAY]: "Tue",
  [DayOfWeek.WEDNESDAY]: "Wed",
  [DayOfWeek.THURSDAY]: "Thu",
  [DayOfWeek.FRIDAY]: "Fri",
  [DayOfWeek.SATURDAY]: "Sat",
  [DayOfWeek.SUNDAY]: "Sun",
};

const DAY_ORDER = [
  DayOfWeek.MONDAY,
  DayOfWeek.TUESDAY,
  DayOfWeek.WEDNESDAY,
  DayOfWeek.THURSDAY,
  DayOfWeek.FRIDAY,
  DayOfWeek.SATURDAY,
  DayOfWeek.SUNDAY,
];

/**
 * Formats fixed days with abbreviations and consecutive day ranges
 * Example: ["monday", "tuesday", "wednesday", "friday"] -> "Mon-Wed, Fri"
 */
export function formatFixedDays(daysOfWeek: string[]): string {
  if (daysOfWeek.length === 0) {
    return "";
  }

  // Sort days by their order in the week
  const sortedDays = [...daysOfWeek].sort(
    (a, b) =>
      DAY_ORDER.indexOf(a.toUpperCase() as DayOfWeek) -
      DAY_ORDER.indexOf(b.toUpperCase() as DayOfWeek)
  );

  const groups: string[][] = [];
  let currentGroup: string[] = [sortedDays[0]];

  for (let index = 1; index < sortedDays.length; index += 1) {
    const currentDayIndex = DAY_ORDER.indexOf(sortedDays[index].toUpperCase() as DayOfWeek);
    const previousDayIndex = DAY_ORDER.indexOf(sortedDays[index - 1].toUpperCase() as DayOfWeek);

    if (currentDayIndex === previousDayIndex + 1) {
      // Consecutive day
      currentGroup.push(sortedDays[index]);
    } else {
      // Non-consecutive, start new group
      groups.push(currentGroup);
      currentGroup = [sortedDays[index]];
    }
  }

  groups.push(currentGroup);

  // Format each group
  return groups
    .map((group) => {
      if (group.length >= 3) {
        // Show range for 3+ consecutive days
        const firstDay = DAY_ABBREVIATIONS[group[0].toUpperCase()];
        const lastDay = group.at(-1);
        const lastDayAbbreviation = lastDay ? DAY_ABBREVIATIONS[lastDay.toUpperCase()] : "";
        return `${firstDay}-${lastDayAbbreviation}`;
      }

      // Show individual days for 1-2 days
      return group.map((day) => DAY_ABBREVIATIONS[day.toUpperCase()]).join(", ");
    })
    .join(", ");
}

/**
 * Formats flexible schedule with day count and workdays requirement
 * Example: (3, "weekdays") -> "3 days / wk, weekdays only"
 * Example: (4, "both") -> "4 days / wk, mix of (weekdays, weekends)"
 */
export function formatFlexibleSchedule(
  daysPerWeek: number,
  workdaysRequired?: "weekdays" | "weekends" | "both"
): string {
  const dayLabel = daysPerWeek === 1 ? "day" : "days";
  const basePart = `${daysPerWeek} ${dayLabel} / wk`;

  if (!workdaysRequired) {
    return basePart;
  }

  let workdaysPart: string;
  if (workdaysRequired === "both") {
    workdaysPart = "mix of (weekdays, weekends)";
  } else if (workdaysRequired === "weekdays") {
    workdaysPart = "weekdays only";
  } else {
    workdaysPart = "weekends only";
  }

  return `${basePart}, ${workdaysPart}`;
}
